/* eslint-disable no-console */
import type {
  CouponCodeAction,
  MultiThresholdTick,
  PromoThreshold,
  Promotions
} from "@shared/graphql.types";
import type {
  PromoSectionProps,
  PromoTileProps,
  AlertProps
} from "@shared/ui.components";
import { addPromoCode, removeCtPromoCode } from "lib/api";
import type { SegmentTrackPageElementClickedEvent } from "@beautypie/segment/Track/PageElementClicked";
import { track, trackClick } from "tracking";
import type { SegmentTrackPageElementViewedEvent } from "@beautypie/segment/Track/PageElementViewed";
import type { StoreType } from "@beautypie/core";
import { getStore } from "@shared/utils.locale";

function isExternalLink(url: string): boolean {
  try {
    const linkUrl = new URL(url, window.location.origin);
    return linkUrl.origin !== window.location.origin;
  } catch {
    return false; // fallback if invalid URL
  }
}

const errorTitle = "Something went wrong";
const PROMO_TILE_TRACKING_VIEW_THRESHOLD = "0.75";

const contactLinks: Record<
  StoreType,
  Pick<AlertProps, "linkText" | "linkHref">
> = {
  uk: {
    linkText: "Get Help",
    linkHref:
      "https://beautypiehelp.zendesk.com/hc/en-gb/requests/new?_gl=1*g2wha2*_up*MQ..*_ga*MjEzNzE1NDM3MC4xNzYwMDE4NDQ4*_ga_76YDC86ZZH*czE3NjAwMTg0NDckbzEkZzEkdDE3NjAwMTg0NTUkajUzJGwwJGgw"
  },
  us: {
    linkText: "Get Help",
    linkHref:
      "https://beautypiehelp.zendesk.com/hc/en-us/requests/new?_gl=1%2Ag2wha2%2A_up%2AMQ..%2A_ga%2AMjEzNzE1NDM3MC4xNzYwMDE4NDQ4%2A_ga_76YDC86ZZH%2AczE3NjAwMTg0NDckbzEkZzEkdDE3NjAwMTg0NTUkajUzJGwwJGgw"
  }
};

const handleCouponActivation = async (
  couponCode: string,
  basketId: string | null | undefined,
  onMessage?: (message: AlertProps) => void
): Promise<void> => {
  if (!basketId) {
    onMessage?.({
      title: errorTitle,
      body: "Please add items to your basket before applying promo codes.",
      variant: "info"
    });
    return;
  }

  const store = getStore();

  try {
    await addPromoCode(couponCode, {
      onCodeApplied: (appliedDiscount) => {
        track({
          event: "Coupon Applied",
          properties: {
            coupon_id: couponCode,
            coupon_name: appliedDiscount.name,
            discount: appliedDiscount.value
          }
        });
        onMessage?.({
          body: `Promo code ${couponCode} applied successfully! ${appliedDiscount.name}`,
          title: "Success",
          variant: "success"
        });
      },
      onCodeRejected: (rejectedDiscount) => {
        track({
          event: "Coupon Denied",
          properties: {
            coupon_id: couponCode,
            reason: rejectedDiscount.state
          }
        });
        onMessage?.({
          body: `Promo code ${couponCode} was rejected: ${rejectedDiscount.state}. If this error continues, please reach out to Member Happiness.`,
          title: errorTitle,
          variant: "error",
          ...contactLinks[store]
        });
      }
    });
  } catch (error) {
    onMessage?.({
      body: `Failed to apply promo code ${couponCode}. If this error continues, please reach out to Member Happiness.`,
      title: errorTitle,
      variant: "error",
      ...contactLinks[store]
    });
  }
};

const handleCouponRemoval = async (
  couponCode: string,
  onMessage?: (message: AlertProps) => void
): Promise<void> => {
  const store = getStore();

  try {
    await removeCtPromoCode(couponCode);
    onMessage?.({
      body: `Promo code ${couponCode} removed successfully.`,
      title: "Success",
      variant: "success"
    });
  } catch (error) {
    onMessage?.({
      body: `Failed to remove promo code ${couponCode}. If this error continues, please reach out to Member Happiness.`,
      title: errorTitle,
      variant: "error",
      ...contactLinks[store]
    });
  }
};

type CreateCouponTrackingEventProps = {
  promo: NonNullable<Promotions["promotions"][0]>;
  index: number;
  totalPromotions: number;
};

const createCouponTrackingEvent = ({
  promo,
  index,
  totalPromotions
}: CreateCouponTrackingEventProps): SegmentTrackPageElementClickedEvent => {
  const isBasket = ["/basket", "/us/basket"].some((p) =>
    window.location.pathname.startsWith(p)
  );

  return {
    event: "Page Element Clicked",
    properties: {
      module_content: `${promo.id}`,
      location_type: isBasket ? "basket_promo" : "pdp_promo",
      module_content_details: (promo.actionV2 as CouponCodeAction).isActivated
        ? "qualified:1/1"
        : "qualified:0/1",
      module_content2: (promo.actionV2 as CouponCodeAction).isActivated
        ? "action:deactivate"
        : "action:activate",
      ...(totalPromotions > 1 && {
        location_type_details: `carousel:${index + 1}/${totalPromotions}`
      })
    }
  };
};

type PromoDetailsProps = {
  promotions: Promotions | undefined;
  basketId: string | null | undefined;
  onMessage?: (message: AlertProps) => void;
};

function isMultiThreshold(
  threshold: PromoThreshold
): threshold is PromoThreshold & {
  ticks: [MultiThresholdTick, MultiThresholdTick, ...MultiThresholdTick[]];
} {
  if (!threshold.ticks) {
    return false;
  } else {
    if (threshold.ticks.length > 1) {
      return true;
    } else {
      throw new Error("Multi threshold promo must have at least 2 threholds");
    }
  }
}

function calculateProgressFromThreshold(
  threshold: PromoThreshold
): PromoTileProps["progress"] {
  let thresholdObj;

  if (isMultiThreshold(threshold)) {
    thresholdObj = threshold.ticks.map((tick) => {
      return {
        ...(!!tick.reward && { reward: tick.reward }),
        isGift: !tick.reward,
        value: tick.label
      };
    });
  } else {
    thresholdObj = {
      isGift: false,
      value: threshold.value
    };
  }

  return {
    current: threshold.current,
    threshold: thresholdObj
  };
}

export function toPromoSectionProps({
  promotions,
  basketId,
  onMessage
}: PromoDetailsProps): PromoSectionProps {
  const isBasket = ["/basket", "/us/basket"].some((p) =>
    window.location.pathname.startsWith(p)
  );

  const promoSectionProps: PromoSectionProps = {
    title: promotions?.sectionTitle || "",
    description: promotions?.sectionDescription || "",
    variant: "pdp",
    promos: promotions?.promotions
      .filter((promo) => !!promo)
      .map((promo, index) => {
        const result: PromoTileProps = {
          title: promo.title,
          description: promo.description,
          layout: "padding",
          linkAlign: "start",
          linkStyle: {
            noPadding: true
          },
          progress: promo.threshold
            ? calculateProgressFromThreshold(promo.threshold)
            : undefined,
          image: promo.image
            ? {
                src: promo.image.src,
                altText: promo.image.altText
              }
            : undefined,
          successIcon:
            !!promo.threshold &&
            promo.threshold.current >= promo.threshold.value
        };

        if (promo.actionV2?.__typename === "CouponCodeAction") {
          result.buttonText = promo.actionV2.title;
          result.linkAsButton = true;
          result.successIcon = promo.actionV2.isActivated;
          result.onClick = async () => {
            // Track the coupon click event
            const trackingEvent = createCouponTrackingEvent({
              promo,
              index,
              totalPromotions: promotions.promotions.length
            });
            trackClick(trackingEvent);

            const couponAction = promo.actionV2 as CouponCodeAction;

            // Handle coupon activation/deactivation
            if (!couponAction.isActivated) {
              await handleCouponActivation(
                couponAction.code,
                basketId,
                onMessage
              );
            } else if (couponAction.isActivated && couponAction.isRemovable) {
              await handleCouponRemoval(couponAction.code, onMessage);
            }
          };
        } else if (promo.actionV2?.__typename === "NavigateAction") {
          result.buttonText = promo.actionV2.title;
          result.linkAsButton = false;
          result.href = promo.actionV2.webUrlPath;
          result.target = isExternalLink(promo.actionV2.webUrlPath)
            ? "_blank"
            : "_self";
          result.successIcon =
            !!promo.threshold &&
            promo.threshold.current >= promo.threshold.value;
          result.onClick = () => {
            const event: SegmentTrackPageElementClickedEvent = {
              event: "Page Element Clicked",
              properties: {
                module_content: `${promo.id}`,
                location_type: isBasket ? "basket_promo" : "pdp_promo",
                module_content_details: result.successIcon // promo.threshold.current >= promo.threshold.value
                  ? `qualified:1/1`
                  : `qualified:0/1`,
                module_content2: "action:navigate",
                ...(promo.actionV2?.__typename === "NavigateAction" &&
                  promo.actionV2.webUrlPath !== null && {
                    module_content_url: `${window.location.origin}${promo.actionV2.webUrlPath}`
                  }),
                ...(promotions.promotions.length > 1 && {
                  location_type_details: `carousel:${index + 1}/${promotions.promotions.length}`
                })
              }
            };
            trackClick(event);
          };
        }

        result.tracking = {
          viewThreshold: PROMO_TILE_TRACKING_VIEW_THRESHOLD,
          viewed: () => {
            let moduleContentDetails: string | undefined;

            if (promo.threshold) {
              if (isMultiThreshold(promo.threshold)) {
                const thresholdsCount = promo.threshold.ticks.length;
                const qualified = promo.threshold.ticks.findLastIndex(
                  (tick) => tick.value >= tick.label
                );
                moduleContentDetails = `qualified:${qualified + 1}/${thresholdsCount}`;
              } else {
                const qualified =
                  (promo.threshold.current as number) >=
                  (promo.threshold.value as number)
                    ? 1
                    : 0;
                moduleContentDetails = `qualified:${qualified}/${1}`;
              }
            }

            const event: SegmentTrackPageElementViewedEvent = {
              event: "Page Element Viewed",
              properties: {
                module_content: promo.id || "",
                location_type: isBasket ? "basket_promo" : "pdp_promo",
                module_content_details: moduleContentDetails,
                ...(promotions.promotions.length &&
                  promotions.promotions.length > 1 && {
                    location_type_details: `carousel:${index + 1}/${promotions.promotions.length}`
                  })
              }
            };
            track(event);
          }
        };

        return result;
      })
  };

  return promoSectionProps;
}
